import { Component, Input, OnInit, signal, WritableSignal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormControl, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { NgbDateStruct } from '@ng-bootstrap/ng-bootstrap';
import { DatepickerHelper } from '../../../../shared/validators/date-picker-form-validators';
import { setControlDisabled } from '../../../../shared/utils/set-control-disabled';
import { Document } from '@models/documents/document';
import { EntityChangeData } from '@models/documents/entity-change-data';
import { RelationshipType } from '@models/relationships/relationship-type.enum';
import { Relationship } from '@models/relationships/relationship';
import { SelectOption } from '@models/ui-models/select-options';
import { companyNameValidator } from '../../../../shared/validators/compny-name.validator';
import { DatePickerComponent } from '@shared/controls/date-picker/date-picker.component';
import { InputComponent } from '@shared/controls/input/input.component';
import { SelectComponent } from '@shared/controls/select/select.component';
import { CompanyChangePartyName } from '../../../models/company';
import { CustomFormValidators } from "../../../../shared/validators/custom-form-validators";
import {
  directorFirstNameValidatorMsg
} from "../../../../shared/validators/custom-form-validators-messages";
import { BaseStepperFormComponent, StepperFormComponent, StepperFormDescriptionComponent } from "../../stepper-form";
import { DisclaimerComponent } from "@shared/components/commons/disclaimer/disclaimer.component";
import { getForm484LateFeeNotification } from "../../../utils/get-late-fee-notification";

export enum CompanyChangePartyNameStepsEnum {
  FormDescription = 0,
  CompanyMemberName = 1,
}

@Component({
  selector: 'app-company-change-party-name',
  standalone: true,
    imports: [
        ReactiveFormsModule,
        StepperFormComponent,
        StepperFormDescriptionComponent,
        InputComponent,
        DatePickerComponent,
        SelectComponent,
        DisclaimerComponent,
    ],
  templateUrl: './company-change-party-name.component.html',
  styleUrl: '../../stepper-form/base-stepper-component/base-stepper-form.component.scss',
})
export class CompanyChangePartyNameComponent extends BaseStepperFormComponent<CompanyChangePartyNameStepsEnum, CompanyChangePartyName> implements OnInit {
  @Input() entityRelationships: Record<string, Relationship[]> = {};
  @Input() officersChangeNameOptions: SelectOption[] = [];

  protected readonly directorFirstNameValidatorMsg = directorFirstNameValidatorMsg;
  override readonly StepsEnum = CompanyChangePartyNameStepsEnum;
  readonly RelationshipType = RelationshipType;

  minDate!: NgbDateStruct;
  maxDate!: NgbDateStruct;

  isCorporateSecurityHolder: WritableSignal<boolean> = signal<boolean>(false);

  form = new FormGroup({
    changeDate: new FormControl<Date | null>(DatepickerHelper.getToday(), [Validators.required]),
    selectedOfficerId: new FormControl<string | null>(null, [Validators.required]),

    fullName: new FormGroup({
      firstName: new FormControl<string | null>(null, [Validators.required, CustomFormValidators.directorFirstNameValidator]),
      middleName1: new FormControl<string | null>(null, [CustomFormValidators.directorFirstNameValidator]),
      middleName2: new FormControl<string | null>(null, [CustomFormValidators.directorFirstNameValidator]),
      lastName: new FormControl<string | null>(null, [Validators.required, CustomFormValidators.directorFirstNameValidator]),
    }),

    companyName: new FormControl<string | null>(null, [Validators.required, Validators.minLength(7), companyNameValidator()]),
  });

  override stepperForm = new FormGroup({
    [CompanyChangePartyNameStepsEnum.FormDescription]: new FormGroup({}),
    [CompanyChangePartyNameStepsEnum.CompanyMemberName]: this.form
  });

  constructor() {
    super();
    this.setupSteps(CompanyChangePartyNameStepsEnum);
  }

  ngOnInit(): void {
    this.minDate = DatepickerHelper.getDateOfEstablishmentMinDate(this.companyChangeData.dateOfEstablishment);
    this.maxDate = DatepickerHelper.getNextNYearsStruct(10);

    this.listenSelectedOfficeholderIdChanges();
    this.listenChangeDateChange(this.form.controls.changeDate, getForm484LateFeeNotification);
    this.setupChange();
  }

  override afterSubmit(changes: EntityChangeData[]): void {
    this.setupChange(changes[0] as CompanyChangePartyName);
  }

  override setupChange(change: CompanyChangePartyName = this.formModel): void {
    const selectedRelationshipIds = change.relationshipIds[0];
    const selectedOfficerId = Object.entries(this.entityRelationships)
      .find(([key, relashionships]) => relashionships
        .some(relashionship => relashionship.relationshipId === selectedRelationshipIds)
      )?.[0] ?? null; // it's a key

    this.form.patchValue({
      ...change,
      selectedOfficerId,
      fullName: change,
    });

    this.form.updateValueAndValidity();
  }

  override buildDocument(): Document | null {
    const fullName = this.form.controls.fullName.controls.firstName.value + ' ' + this.form.controls.fullName.controls.lastName.value;
    const relationshipIds = this.entityRelationships[this.form.value.selectedOfficerId!]?.map(officer => officer.relationshipId) ?? [this.form.value.selectedOfficerId];

    const changes = new CompanyChangePartyName({
      ...this.form.value.fullName as Partial<CompanyChangePartyName>,
      relationshipIds,
      description: 'Name change for ' + fullName,
      companyName: this.form.value.companyName ?? '',
      changeDate: this.form.value.changeDate!,
    });

    try {
      return new Document({
        changes: [changes],
        entityId: this.companyChangeData.entityId,
        type: 'c:484',
        documentId: this.companyChangeData?.documentId,
      });
    } catch (error) {
      console.warn(error);
      this.toastr.error('Failed to create Document.', 'Error');
      return null;
    }
  }

  private listenSelectedOfficeholderIdChanges(): void {
    this.form.controls.selectedOfficerId.valueChanges
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((selectedOfficerId) => {
        this.isCorporateSecurityHolder.set(!!this.entityRelationships[selectedOfficerId!]?.[0]?.entityDataOverride);

        if (!selectedOfficerId) return;

        const relatedRelationships = this.entityRelationships[selectedOfficerId] ?? [];

        setControlDisabled(this.form.controls.fullName, this.isCorporateSecurityHolder());
        setControlDisabled(this.form.controls.companyName, !this.isCorporateSecurityHolder());

        if (!this.isCorporateSecurityHolder()) {
          this.form.controls.fullName.patchValue(relatedRelationships[0].individualDataOverride!);
        }
      });
  }
}
